import { localizationService } from "../../localization";
import { BackSide, Group, NormalBlending, Object3D, ShaderMaterial, Mesh, RepeatWrapping, BufferGeometry, BufferAttribute } from "three";
import { TOOLTIP_FRAGMENT_SHADER, TOOLTIP_VERTEX_SHADER } from "../../selection/highlighting/selection.shaders";
import { Matrix4, Vector3 } from "flavr-math";
import { AvatarLoadedMessage, LoadAvatarMessage } from "../../../messages/rendering/RendererMessages";
import { FacetRenderer } from "../renderable/Renderable";
import { RenderOrder } from "../RenderOrder";
import { AvatarStyle, TooltipTextAlign } from "flavr-common";
import { textureLoaderService } from "../texture/TextureLoaderService";
import { clockService } from "../../xr/ClockService";
import { Text } from "troika-three-text";
const tempTransform = new Matrix4();
const TOOLTIP_TEXT_SCALE = 0.3;
const TOOLTIP_SCALE = new Vector3(0.0015, 0.0015, 0.0015);
const AVATAR_HEXAGON_POSITION = new Vector3(0, -1, 25);
const tooltipBackgroundGeometry = buildTooltipBackgroundGeometry();
const tooltipOrigin = new Vector3();
function buildTooltipBackgroundGeometry() {
    var _a, _b, _c;
    const geometry = new BufferGeometry();
    // x and y of vertices
    const xyz = [-1, -1, 0, -1, 1, 0, 1, 1, 0, 1, -1, 0, 0, 0, 1];
    const positions = new Float32Array(xyz.length * 3);
    positions.set(xyz);
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3, 1, 0, 4, 2, 1, 4, 3, 2, 4, 0, 3, 4]);
    // create uv coordinates
    const uvs = [];
    const colors = [];
    for (let f = 0; f < geometry.getIndex().count / 3; f += 3) {
        const faceIndices = new Vector3((_a = geometry.getIndex()) === null || _a === void 0 ? void 0 : _a.getX(f), (_b = geometry.getIndex()) === null || _b === void 0 ? void 0 : _b.getX(f + 1), (_c = geometry.getIndex()) === null || _c === void 0 ? void 0 : _c.getX(f + 2));
        if (faceIndices.z === 4) {
            // pyramid side
            uvs.push(0, 1, 1, 1, 0.5, 0);
            // set vertex Colors
            colors.push(geometry.getAttribute("position").getY(faceIndices.x) * -0.5 + 0.5, 0, 0, geometry.getAttribute("position").getY(faceIndices.y) * -0.5 + 0.5, 0, 0, 1, 0, 0);
        }
        else {
            // pyramid bottom = background for the text
            uvs.push((geometry.getAttribute("position").getX(faceIndices.x) + 1) / 2, 1 - (geometry.getAttribute("position").getY(faceIndices.x) + 1) / 2, (geometry.getAttribute("position").getX(faceIndices.y) + 1) / 2, 1 - (geometry.getAttribute("position").getY(faceIndices.y) + 1) / 2, (geometry.getAttribute("position").getX(faceIndices.z) + 1) / 2, 1 - (geometry.getAttribute("position").getY(faceIndices.z) + 1) / 2);
            // set vertex Colors
            colors.push(geometry.getAttribute("position").getY(faceIndices.x) * -0.5 + 0.5, 0, 0, geometry.getAttribute("position").getY(faceIndices.y) * -0.5 + 0.5, 0, 0, geometry.getAttribute("position").getY(faceIndices.z) * -0.5 + 0.5);
        }
    }
    const colorArray = new Float32Array(xyz.length * 3);
    colorArray.set(colors);
    geometry.setAttribute("color", new BufferAttribute(colorArray, 3));
    const uvArray = new Float32Array(xyz.length * 2);
    uvArray.set(uvs);
    geometry.setAttribute("uv", new BufferAttribute(uvArray, 2));
    return geometry;
}
const tooltipBackgroundMaterial = new ShaderMaterial({
    uniforms: {
        time: { value: 0.0 }
    },
    vertexShader: TOOLTIP_VERTEX_SHADER,
    fragmentShader: TOOLTIP_FRAGMENT_SHADER,
    side: BackSide,
    blending: NormalBlending,
    vertexColors: true,
    transparent: true
});
textureLoaderService.loadTexture("img/holo.jpg", (texture) => {
    texture.wrapT = RepeatWrapping;
    tooltipBackgroundMaterial.uniforms.texture = {
        value: texture
    };
}, () => { }, () => {
    console.error("[renderer] Could not load img/holo.jpg");
});
clockService.addShaderMaterial("tooltipBackground", tooltipBackgroundMaterial, {
    factor: 10
});
export class TooltipRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.tooltipBackground = Mesh.prototype;
        this.textMeshes = [];
        this.avatarMesh = Object3D.prototype;
        this.tooltipGroup = new Group();
        this.tooltipGroup.name = "tooltip";
        this.tooltipGroup.rotation.x = Math.PI / 3;
        this.tooltipGroup.scale.set(0.0025, 0.0025, 0.0025);
        this.tooltipGroup.position.set(0, 0.01, 0);
        this.tooltipGroup.matrixAutoUpdate = false;
        this.tooltipGroup.frustumCulled = false;
        this.tooltipBackgroundGeometry = tooltipBackgroundGeometry.clone();
    }
    destroy() {
        this.destroyTooltip();
        this.tooltipGroup && this.tooltipGroup.parent && this.tooltipGroup.parent.remove(this.tooltipGroup);
        this.tooltipBackgroundGeometry.dispose();
    }
    destroyTooltip() {
        if (this.textMeshes.length > 0) {
            for (let i = 0; i < this.textMeshes.length; ++i) {
                this.tooltipGroup.remove(this.textMeshes[i]);
            }
            this.textMeshes = [];
        }
        if (this.avatarMesh) {
            this.tooltipGroup.remove(this.avatarMesh);
        }
        if (this.tooltipBackground) {
            this.tooltipGroup.remove(this.tooltipBackground);
        }
    }
    setVisible(visible) {
        this.tooltipGroup.visible = visible;
    }
    updateFacet(facet) {
        this.destroyTooltip();
        const text = new Text();
        text.text = localizationService.getTranslation(facet.label);
        text.anchorX = "center";
        text.anchorY = "middle";
        switch (facet.align) {
            case TooltipTextAlign.Left:
                text.textAlign = "left";
                break;
            case TooltipTextAlign.Right:
                text.textAlign = "right";
                break;
            case TooltipTextAlign.Center:
                text.textAlign = "center";
                break;
        }
        text.matrixAutoUpdate = false;
        text.frustumCulled = false;
        text.name = "text";
        text.scale.setScalar(TOOLTIP_TEXT_SCALE);
        text.rotation.x = Math.PI / 2;
        text.sync(() => {
            var _a;
            const boundingBoxSize = new Vector3();
            (_a = text.geometry.boundingBox) === null || _a === void 0 ? void 0 : _a.getSize(boundingBoxSize);
            const textWidth = boundingBoxSize.x * TOOLTIP_TEXT_SCALE;
            const textHeight = boundingBoxSize.y * TOOLTIP_TEXT_SCALE;
            text.position.y = 2;
            text.position.z = 7.5;
            // center line vertically and maybe put down under avatar image
            text.position.z -= textHeight / 2 + (facet.avatar ? -15 : 0);
            text.renderOrder = RenderOrder.TOOLTIP_TEXT;
            this.tooltipGroup.add(text);
            this.textMeshes.push(text);
            this.tooltipBackground = new Mesh(this.tooltipBackgroundGeometry, tooltipBackgroundMaterial);
            this.tooltipBackground.matrixAutoUpdate = false;
            this.tooltipBackground.frustumCulled = false;
            this.tooltipBackground.position.setScalar(0);
            this.tooltipBackground.renderOrder = RenderOrder.TOOLTIP_BACKGROUND;
            this.tooltipBackground.rotation.x = -Math.PI / 2;
            // width is text width + a little margin
            this.tooltipBackground.scale.x = textWidth / 2 + 10;
            // height same + something for avatar image
            this.tooltipBackground.scale.y = textHeight + (facet.avatar ? 10 : 0);
            this.tooltipBackground.scale.z = -20;
            this.tooltipBackground.updateMatrix();
            // tip of the pyramid transformed to local coordinates
            const origin = tooltipOrigin
                .copy(new Vector3(this.tooltipBackgroundGeometry.getAttribute("position").getX(4), this.tooltipBackgroundGeometry.getAttribute("position").getY(4), this.tooltipBackgroundGeometry.getAttribute("position").getZ(4)))
                .applyMatrix4(this.tooltipBackground.matrix);
            // move the text accordingly
            this.tooltipGroup.children.forEach((child) => {
                child.position.sub(origin);
                child.updateMatrix();
            });
            // and the background itself,
            this.tooltipBackground.position.sub(origin);
            this.tooltipBackground.updateMatrix();
            if (facet.avatar) {
                this.owner.renderer
                    .getConnection()
                    // Todo: perhaps change this. This is more like a profile-picture than a real Avatar.
                    .ask(new LoadAvatarMessage(facet.avatar, AvatarStyle.Monitor), (messages) => {
                    const message = messages.length === 1 ? messages[0] : undefined;
                    if (message && AvatarLoadedMessage.is(message)) {
                        this.avatarLoaded(message);
                    }
                    else {
                        console.error(`Failed on load avatar ${facet.avatar} with model uid ${this.owner.modelUID.toString()}`);
                    }
                });
            }
            this.tooltipGroup.add(this.tooltipBackground);
            this.tooltipGroup.scale.copy(TOOLTIP_SCALE);
            this.tooltipGroup.updateMatrix();
            this.owner.renderer.addToScene(this.tooltipGroup);
        });
    }
    avatarLoaded(message) {
        if (this.avatarMesh) {
            this.tooltipGroup.remove(this.avatarMesh);
        }
        this.avatarMesh = message.headMesh.clone();
        this.avatarMesh.matrixAutoUpdate = false;
        this.avatarMesh.frustumCulled = false;
        this.avatarMesh.receiveShadow = false;
        this.avatarMesh.castShadow = false;
        this.avatarMesh.position.copy(tooltipOrigin).negate().sub(AVATAR_HEXAGON_POSITION);
        this.avatarMesh.updateMatrix();
        this.tooltipGroup.add(this.avatarMesh);
    }
    updateTransform() {
        // Multiply local matrix
        tempTransform.copy(this.owner.matrixWorld);
        tempTransform.multiplyMatrices(tempTransform, this.tooltipGroup.matrix);
        this.tooltipGroup.matrixWorld.copy(tempTransform);
        this.tooltipGroup.matrixWorldNeedsUpdate = false;
    }
}
